<template>
  <div>
    <div v-if="candidate"
         class="bg-white shadow overflow-hidden sm:rounded-lg">
      <div class="px-4 py-5 border-b border-gray-200 sm:px-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900">
          {{$t('candidates.details.title')}}
        </h3>
        <p class="mt-1 max-w-2xl text-sm leading-5 text-gray-500">
          {{$t('candidates.details.description')}}
        </p>
      </div>
      <div>
        <dl>
          <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm leading-5 font-medium text-gray-500">
              {{$t('generic.name')}}
            </dt>
            <dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2 flex items-center">
              <base-avatar class="mr-2" :src="candidate.user.image" :name="candidate.user.given_name"/> {{candidate.given_name}} {{candidate.family_name}}
            </dd>
          </div>
          <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm leading-5 font-medium text-gray-500">
              {{$t('generic.email')}}
            </dt>
            <dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
              {{candidate.email}}
            </dd>
          </div>
          <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm leading-5 font-medium text-gray-500">
              {{$t('generic.createdAt')}}
            </dt>
            <dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
              {{$formatDate(candidate.created_at, FRIENDLY_DATETIME_FORMAT)}}
            </dd>
          </div>
          <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm leading-5 font-medium text-gray-500">
              {{$t('generic.resumes')}}
            </dt>
            <dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
              <ul class="border border-gray-200 rounded-md">
                <li v-if="candidate.resumes.length === 0"
                    class="pl-3 pr-4 py-3 flex items-center justify-between text-sm leading-5">
                  {{$t('candidate.resumes.noResumes')}}
                </li>
                <li v-for="(resume, index) in candidate.resumes"
                    :key="resume.id"
                    class="pl-3 pr-4 py-3 flex items-center justify-between text-sm leading-5"
                    :class="{'border-t border-gray-200': index > 0}"
                >
                  <div class="w-0 flex-1 flex items-center">
                    <svg class="flex-shrink-0 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd"
                            d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
                            clip-rule="evenodd"/>
                    </svg>
                    <span class="ml-2 flex-1 w-0 truncate">
                    {{$formatDate(resume.created_at, FRIENDLY_DATETIME_FORMAT)}}
                  </span>
                  </div>
                  <div v-if="resume.file_uuid"
                       class="ml-4 flex-shrink-0">
                    <a href="#"
                       @click.prevent="downloadFile(resume)"
                       class="font-medium text-primary hover:text-underline transition duration-150 ease-in-out">
                      <i v-if="resume.loading" class="el-icon-loading"></i>
                      {{$t('generic.download')}}
                    </a>
                  </div>
                </li>
              </ul>
            </dd>
          </div>
          <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm leading-5 font-medium text-gray-500">
              {{$t('generic.jobAlerts')}}
            </dt>
            <dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
              <ul class="border border-gray-200 rounded-md">
                <li v-if="candidate.job_alerts.length === 0"
                    class="pl-3 pr-4 py-3 flex items-center justify-between text-sm leading-5">
                  {{$t('candidate.alerts.noAlerts')}}
                </li>
                <li v-for="(alert, index) in candidate.job_alerts"
                    :key="alert.id"
                    class="pl-3 pr-4 py-3 flex items-center justify-between text-sm leading-5"
                    :class="{'border-t border-gray-200': index > 0}"
                >
                  <div class="w-0 flex-1 flex items-center">
                    <search-icon class="flex-shrink-0 h-5 w-5 text-gray-400"/>
                    <span class="mx-2 w-24 truncate">
                      {{get(alert, 'query.search') || $t('alerts.allJobs')}}
                    </span>
                    <div v-if="get(alert, 'query.location')"
                         class="flex items-center ml-3">
                      <map-pin-icon class="flex-shrink-0 h-5 w-5 text-gray-400"/>
                      <span class="ml-1">
                        {{get(alert, 'query.location')}}
                      </span>
                    </div>
                    <span
                      class="ml-auto inline-block py-1 px-3 text-xs leading-4 rounded-full bg-gray-100 group-hover:bg-gray-200 group-focus:bg-gray-200">
                      {{alert.frequency}}
                    </span>
                    <status-badge class="ml-2 mb-0" :status="alert.active ? 'active': 'inactive'"></status-badge>
                  </div>
                  <div v-if="alert.file_uuid"
                       class="ml-4 flex-shrink-0">
                    <a href="#"
                       @click.prevent="downloadFile(resume)"
                       class="font-medium text-primary hover:text-underline transition duration-150 ease-in-out">
                      <i v-if="alert.loading" class="el-icon-loading"></i>
                      {{$t('generic.download')}}
                    </a>
                  </div>
                </li>
              </ul>
            </dd>
          </div>
          <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt class="text-sm leading-5 font-medium text-gray-500">
              GDPR Data
            </dt>
            <dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
              <div class="ml-4 flex-shrink-0">
                <a href="#"
                   @click.prevent="downloadData"
                   class="font-medium text-primary hover:text-underline transition duration-150 ease-in-out">
                  <i v-if="loading" class="el-icon-loading"></i>
                  {{$t('generic.download')}}
                </a>
              </div>
            </dd>
          </div>
        </dl>
      </div>
    </div>
  </div>
</template>
<script>
  import get from 'lodash/get'
  import CandidateService from 'src/services/CandidateService'
  import FileService from 'src/services/FileService'
  import { BellIcon, SearchIcon, MapPinIcon } from 'vue-feather-icons'
  import { FRIENDLY_DATETIME_FORMAT } from 'src/config/dateUtils'
  export default {
    components: {
      BellIcon,
      SearchIcon,
      MapPinIcon
    },
    data () {
      return {
        FRIENDLY_DATETIME_FORMAT,
        loading: false
      }
    },
    computed: {
      candidate () {
        return this.$store.state.candidate.currentCandidate
      }
    },
    methods: {
      get,
      async downloadData () {
        try {
          this.loading = true
          let fileName = `GDPR-data-${this.candidate.given_name} ${this.candidate.family_name}`
          await CandidateService.downloadGDPRData(this.candidate.uuid, fileName)
        } catch (e) {
          console.warn(e)
        }
        this.loading = false
      },
      async downloadFile (resume) {
        try {
          const date = this.$formatDate(resume.created_at, FRIENDLY_DATETIME_FORMAT)
          let name = `CV_${this.candidate.email}_${date}`
          this.$set(resume, 'loading', true)
          await FileService.downloadFile(resume.file_token, name)
        } catch (e) {
          this.$notify({
            type: 'danger',
            message: this.$t('resume.download.error')
          })
        } finally {
          this.$set(resume, 'loading', false)
        }
      }
    }
  }
</script>
<style>
</style>
